import React, { useCallback, useEffect, useState } from "react";
import {
  KeyRound, CheckCircle2, XCircle, Clock, Loader2, ShieldAlert, History,
  RefreshCw, Mail, Phone, User, Briefcase, Info,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchIdentityChanges, approveIdentityChange, rejectIdentityChange,
  fetchSecurityConfig, updateSecurityConfig,
  notifyApprovalsChanged,
} from "@admin/services/adminApi";

const STATUS_TABS = [
  { key: "pending_approval", label: "Pending approval", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", icon: XCircle },
  { key: "all", label: "All history", icon: History },
];

const STATUS_PILL = {
  pending_otp: { cls: "bg-amber-100 text-amber-800", label: "Awaiting OTP" },
  pending_approval: { cls: "bg-sky-100 text-sky-800", label: "Pending approval" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  auto_applied: { cls: "bg-emerald-50 text-emerald-700", label: "Auto-applied" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected" },
  cancelled: { cls: "bg-slate-100 text-slate-500", label: "Cancelled" },
  expired: { cls: "bg-slate-100 text-slate-500", label: "Expired" },
};

const ROLE_ICON = { user: User, lawyer: Briefcase, admin: ShieldAlert };

function Pill({ status }) {
  const p = STATUS_PILL[status] || { cls: "bg-slate-100 text-slate-700", label: status };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.cls}`}>{p.label}</span>;
}

function DecisionModal({ open, item, action, onClose, onSubmit, busy }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (!open) setNote(""); }, [open]);
  if (!open || !item) return null;
  const isReject = action === "reject";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="change-decision-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-5 shadow-xl">
        <h3 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          {isReject ? "Reject change request" : "Approve change request"}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {isReject
            ? "Rejection is final. The requester will need to open a new request. Provide a clear reason."
            : "Approval applies the change immediately: updates the record, refreshes the identity registry, invalidates all active sessions, and notifies both old and new contacts."}
        </p>
        <div className="mt-3 text-xs text-slate-600">
          <p><strong>{item.subject_type}</strong> is changing <strong>{item.field}</strong>:</p>
          <p className="mt-1 text-slate-500">
            <span className="line-through">{item.old_value_masked}</span> → <strong>{item.new_value_masked}</strong>
          </p>
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={isReject ? "Reason (required)" : "Approver note (optional)"}
          rows={3}
          data-testid="change-note-input"
          className="mt-3 rounded-xl"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="change-cancel">Cancel</Button>
          <Button
            onClick={() => onSubmit(note.trim())}
            disabled={busy || (isReject && !note.trim())}
            data-testid="change-confirm"
            className={isReject ? "bg-rose-600 hover:bg-rose-700 text-white rounded-xl" : "adv-btn-primary rounded-xl"}
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            {isReject ? "Reject" : "Approve"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequestRow({ item, onApprove, onReject }) {
  const Icon = ROLE_ICON[item.subject_type] || User;
  const FieldIcon = item.field === "email" ? Mail : Phone;
  const canDecide = item.status === "pending_approval";
  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-white" data-testid={`change-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Pill status={item.status} />
            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0A2540] capitalize">
              <Icon size={12} /> {item.subject_type}
            </span>
            <span className="text-[10px] text-slate-400">{item.created_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <p className="text-sm text-[#0A2540]">
            <FieldIcon size={12} className="inline mr-1 text-slate-400" />
            <span className="line-through text-slate-400">{item.old_value_masked}</span>
            <span className="mx-2 text-slate-300">→</span>
            <strong>{item.new_value_masked}</strong>
          </p>
          <div className="mt-1 text-[11px] text-slate-500">
            subject: {item.subject_id?.slice(0, 12)} · {item.subject_email}
          </div>
          {item.reason && (
            <p className="mt-1 text-xs text-slate-600 italic">&ldquo;{item.reason}&rdquo;</p>
          )}
          {item.approver_notes && (
            <p className="mt-1 text-xs text-slate-600">
              <strong>Approver note:</strong> {item.approver_notes}
            </p>
          )}
          {(item.old_verified || item.new_verified) && (
            <p className="mt-1 text-[11px] text-slate-500">
              OTP verified — old: {item.old_verified ? "✓" : "✗"} · new: {item.new_verified ? "✓" : "✗"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canDecide ? (
            <>
              <Button size="sm" variant="outline" onClick={() => onReject(item)} data-testid={`change-reject-${item.id}`}>
                <XCircle size={14} className="mr-1" /> Reject
              </Button>
              <Button size="sm" onClick={() => onApprove(item)} className="adv-btn-primary" data-testid={`change-approve-${item.id}`}>
                <CheckCircle2 size={14} className="mr-1" /> Approve
              </Button>
            </>
          ) : (
            <span className="text-[11px] text-slate-400">
              {item.applied_at ? `applied ${item.applied_at.slice(0, 10)}` : ""}
              {item.rejected_at ? `rejected ${item.rejected_at.slice(0, 10)}` : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminIdentityChanges() {
  const [tab, setTab] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null); // {action, item}
  const [cfg, setCfg] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (tab !== "all") params.status = tab;
      const d = await fetchIdentityChanges(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
      notifyApprovalsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load requests"); }
    finally { setLoading(false); }
  }, [tab]);

  const loadCfg = useCallback(async () => {
    try { setCfg(await fetchSecurityConfig()); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const submitDecision = async (note) => {
    if (!decision) return;
    setBusy(true);
    try {
      if (decision.action === "approve") await approveIdentityChange(decision.item.id, note);
      else await rejectIdentityChange(decision.item.id, note);
      toast.success(decision.action === "approve" ? "Change applied" : "Request rejected");
      setDecision(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleAutoApply = async () => {
    try {
      const next = !cfg?.lawyer_identity_change_auto_apply;
      const fresh = await updateSecurityConfig({ lawyer_identity_change_auto_apply: next });
      setCfg(fresh);
      toast.success(next ? "Lawyer auto-apply ON" : "Lawyer auto-apply OFF");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pendingCount = total && tab === "pending_approval" ? total : null;

  return (
    <>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            <KeyRound size={20} className="inline mr-2 text-[#D4AF37]" />
            Identity Change Requests
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Approve or reject audited email/phone changes for lawyers and admins.
            Approving invalidates all active sessions and notifies both contact endpoints.
          </p>
        </div>
        {cfg && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleAutoApply}
                  data-testid="lawyer-auto-apply-toggle"
                  aria-describedby="lawyer-auto-apply-help"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition ${
                    cfg.lawyer_identity_change_auto_apply
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : "border-slate-200 bg-white text-slate-600 hover:border-[#0A2540]"
                  }`}
                >
                  <RefreshCw size={12} />
                  Lawyer auto-apply: {cfg.lawyer_identity_change_auto_apply ? "ON" : "OFF"}
                  <Info size={12} className="opacity-60" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed"
                id="lawyer-auto-apply-help" data-testid="lawyer-auto-apply-tooltip">
                <p className="font-bold">Lawyer auto-apply — applies to lawyer email/phone changes only</p>
                <p className="mt-1"><span className="font-bold">ON:</span> once the lawyer verifies OTPs on both the old and new contact, the change is applied immediately (record updated, all sessions logged out, both contacts notified) and recorded here as auto-applied. No admin review.</p>
                <p className="mt-1"><span className="font-bold">OFF:</span> after both OTPs, the request waits in Pending approval until an admin approves or rejects it.</p>
                <p className="mt-1 text-slate-300">Admin changes always need approval by a different super-admin. Click to toggle (audited).</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`change-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                tab === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "border-slate-200 text-slate-600 hover:border-[#0A2540]"
              }`}
            >
              <Icon size={12} /> {t.label}
              {t.key === "pending_approval" && pendingCount != null && pendingCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto text-[11px] text-slate-400 uppercase tracking-widest font-bold">
          Showing {items.length} of {total}
        </div>
      </div>

      {loading && <p className="text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="text-center bg-slate-50 border border-slate-200 rounded-2xl p-10" data-testid="change-empty">
          <ShieldAlert size={28} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-500">
            {tab === "pending_approval" ? "No pending approvals — you're all clear." : "No requests match this filter."}
          </p>
        </div>
      )}
      <div className="space-y-3">
        {items.map((item) => (
          <RequestRow
            key={item.id}
            item={item}
            onApprove={(x) => setDecision({ action: "approve", item: x })}
            onReject={(x) => setDecision({ action: "reject", item: x })}
          />
        ))}
      </div>

      <DecisionModal
        open={!!decision}
        item={decision?.item}
        action={decision?.action}
        onClose={() => setDecision(null)}
        onSubmit={submitDecision}
        busy={busy}
      />
    </>
  );
}
